// Lab-side case intake — a multi-step wizard mirroring the clinic portal's, so staff and doctors
// follow the same shape: Patient → Case details → Lab setup → Records → Review.
// Fields map to the workbook's intake row (name, received through, group ID, notes, case type) plus
// the structured extras the spreadsheet never had.
import { useEffect, useMemo, useState } from 'react';
import { WF_STAGE_ROLES as R, WF_REQ_LABELS, WF_REQ_STATUSES, alignerDiscountOf, alignerQuote, isDerivedDueDate, mobileKey, sameMobile, turnaroundDueDate, turnaroundOf, turnaroundSurchargeRupees, type ClinicOption, type WfCase, type WfCaseCreate } from '@alignova/shared';
import { TurnaroundPicker } from '../../ui/TurnaroundPicker.tsx';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ApiError } from '../../lib/api.ts';
import { useAlignerPricing } from '../../lib/useAlignerPricing.ts';
import { WizardFooter, WizardStepper } from '../../ui/Wizard.tsx';
import { uploadCaseFile, useWfConfig, wfApi } from './workflowApi.ts';
import { Btn, ErrorText, Field, Modal, inputCls } from './wfUi.tsx';
import { DiscountInput, QuoteLine } from './DiscountInput.tsx';

const STEPS = ['Patient', 'Case details', 'Lab setup', 'Records', 'Review'] as const;

export function NewWfCaseModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (c: WfCase) => void }) {
  return (
    <Modal open={open} onClose={onClose} title="New case" wide>
      {open && <NewCaseForm onClose={onClose} onCreated={onCreated} />}
    </Modal>
  );
}

function NewCaseForm({ onClose, onCreated }: { onClose: () => void; onCreated: (c: WfCase) => void }) {
  const cfg = useWfConfig();
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'manager';
  const pricing = useAlignerPricing();
  const [step, setStep] = useState(0);
  // The lab is taking the records now, so "now" is what the delivery promise counts from.
  const today = useMemo(() => new Date().toISOString(), []);
  const [f, setF] = useState<WfCaseCreate>({ patientFirstName: '', caseTypes: [], priority: 'normal', dueDate: turnaroundDueDate(new Date().toISOString(), 'normal'), iprStatus: 'not_required', etchingStatus: 'not_required', receivedThrough: 'whatsapp' });
  const [error, setError] = useState<string | null>(null);
  const [dupWarning, setDupWarning] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<string | null>(null);
  const set = <K extends keyof WfCaseCreate>(k: K, v: WfCaseCreate[K]) => setF((p) => ({ ...p, [k]: v }));

  // One "Name" box, but the record keeps first/last separate (search and the workbook rely on it):
  // everything before the first space is the first name, the remainder is the last name.
  const [fullName, setFullName] = useState('');
  const setName = (v: string) => {
    setFullName(v);
    const parts = v.trim().split(/\s+/).filter(Boolean);
    setF((p) => ({ ...p, patientFirstName: parts[0] ?? '', patientLastName: parts.slice(1).join(' ') || null }));
  };

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    // De-dupe by name+size so picking twice doesn't attach the same scan twice.
    setFiles((prev) => {
      const seen = new Set(prev.map((x) => `${x.name}:${x.size}`));
      return [...prev, ...Array.from(list).filter((x) => !seen.has(`${x.name}:${x.size}`))];
    });
  };

  const doctorMatch = useMemo(() => cfg.doctors.find((d) => d.code === (f.groupId ?? '').trim().toUpperCase()), [cfg.doctors, f.groupId]);

  // The lab's real clinics, for the picker. Loaded once — a lab has hundreds, not thousands, and
  // filtering in the browser makes the search instant rather than a request per keystroke.
  const [clinics, setClinics] = useState<ClinicOption[]>([]);
  const [clinicId, setClinicId] = useState<string | null>(null);
  useEffect(() => {
    wfApi.clinics().then(setClinics).catch(() => undefined);
  }, []);

  /**
   * Picking the clinic settles the rest.
   *
   * Its doctor group, contact details and dentist all come from the clinic record, so nobody types
   * a group code from memory — which is how a case ends up billed to the wrong practice. Anything
   * already typed is left alone: the person filling this in knows something the record does not.
   */
  const pickClinic = (c: ClinicOption | null) => {
    setClinicId(c?.id ?? null);
    if (!c) return;
    setF((cur) => ({
      ...cur,
      clinicName: c.name,
      groupId: c.doctorCode ?? cur.groupId,
      doctorId: c.doctorId ?? cur.doctorId,
      doctorName: cur.doctorName || c.dentistName || c.doctorName || null,
      phone: cur.phone || c.mobile || null,
      whatsapp: cur.whatsapp || c.whatsapp || null,
      email: cur.email || c.email || null,
    }));
  };

  const chosenClinic = clinics.find((c) => c.id === clinicId) ?? null;
  const caseTypes = cfg.optionsOf('case_type');
  const nameOk = f.patientFirstName.trim().length > 0;
  const typeOk = f.caseTypes.length > 0;
  const clinicOk = Boolean(clinicId) || Boolean(f.groupId?.trim()) || Boolean(f.doctorId);
  const canContinue = step === 0 ? nameOk && clinicOk : step === 1 ? typeOk : true;

  async function submit(allowDuplicate = false) {
    if (!nameOk) {
      setError('Patient name is required');
      setStep(0);
      return;
    }
    if (!typeOk) {
      setError('Choose a case type');
      setStep(1);
      return;
    }
    // Every case belongs to a clinic: it is who the work came from, who gets billed, and who sees it
    // in their portal. A case filed against nobody is invisible to everyone but the lab.
    if (!clinicOk) {
      setError('Choose the clinic this case came from (or type its Group ID)');
      setStep(0);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // clinicId travels with the case so the server binds the doctor group to that clinic account —
      // that binding is what makes the case appear in the clinic's own portal.
      const payload: WfCaseCreate = { ...f, clinicId, allowDuplicate, doctorName: f.doctorName || doctorMatch?.name || null, clinicName: f.clinicName || doctorMatch?.clinicName || null };
      const c = await wfApi.create(payload);
      // Attach the scans/images to the case that now exists. A failed upload must not discard the
      // case the user just created — report which ones failed and continue.
      const failed: string[] = [];
      for (const [i, file] of files.entries()) {
        setProgress(`Uploading ${i + 1} of ${files.length}: ${file.name}`);
        try {
          await uploadCaseFile(c.id, file);
        } catch {
          failed.push(file.name);
        }
      }
      setProgress(null);
      if (failed.length) {
        setError(`Case ${c.caseNumber} created, but these files didn't upload: ${failed.join(', ')}. Add them from the case page.`);
        setBusy(false);
        return;
      }
      onCreated(c);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'duplicate_case') setDupWarning(err.message);
      else setError(err instanceof Error ? err.message : String(err));
      setStep(STEPS.length - 1);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <WizardStepper steps={STEPS} step={step} onJump={setStep} />
      <ErrorText>{error}</ErrorText>
      {dupWarning && (
        <div className="rounded border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="font-semibold">Possible duplicate</div>
          <div>{dupWarning}</div>
          {isAdmin ? (
            <Btn tone="ghost" className="mt-2" onClick={() => void submit(true)}>
              Create anyway (admin)
            </Btn>
          ) : (
            <div className="mt-1 text-xs">Ask a manager to create it anyway, or open the existing case.</div>
          )}
        </div>
      )}

      {step === 0 && (
        <section className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            <Field
              stacked
              label="Clinic *"
              hint={
                chosenClinic
                  ? chosenClinic.doctorCode
                    ? `Billed to ${chosenClinic.doctorCode}${chosenClinic.doctorName ? ` - ${chosenClinic.doctorName}` : ''}`
                    : 'No Group ID yet - one is created from the clinic code when the case is saved.'
                  : 'Pick the clinic from the list (search by name or mobile number). It is who the case is billed to and who sees it in their portal.'
              }
            >
              <input
                className={inputCls}
                list="wf-clinic-list"
                value={f.clinicName ?? ''}
                onChange={(e) => {
                  const typed = e.target.value;
                  set('clinicName', typed);
                  // A datalist hands back the option's value, so an exact match means they picked
                  // rather than typed — and picking is what fills in everything else.
                  // Two practices can share a name; none share a mobile number, so typing the number
                  // picks the clinic outright — however the digits are spaced.
                  const match =
                    clinics.find((c) => c.name.toLowerCase() === typed.trim().toLowerCase()) ??
                    (mobileKey(typed) ? clinics.find((c) => sameMobile(c.mobile, typed)) : undefined);
                  pickClinic(match ?? null);
                }}
                placeholder={doctorMatch?.clinicName ?? 'Search the lab\u2019s clinics'}
              />
              <datalist id="wf-clinic-list">
                {clinics.map((c) => (
                  <option key={c.id} value={c.name}>
                    {[c.mobile, c.city, c.doctorCode, c.caseCount ? `${c.caseCount} cases` : null].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </datalist>
            </Field>
            <Field stacked label="Name *">
              <input className={inputCls} required autoFocus value={fullName} onChange={(e) => setName(e.target.value)} placeholder="Patient name" />
            </Field>
            <Field stacked label="Group ID" hint={doctorMatch ? `→ ${doctorMatch.name}` : f.groupId ? 'New group - will be created' : 'Filled in for you when you pick the clinic above'}>
              <input className={`${inputCls} font-mono uppercase`} list="wf-doctor-codes" value={f.groupId ?? ''} onChange={(e) => set('groupId', e.target.value.toUpperCase())} placeholder="e.g. DWH-0044" />
              <datalist id="wf-doctor-codes">
                {cfg.doctors.map((d) => (
                  <option key={d.id} value={d.code}>
                    {d.name}
                  </option>
                ))}
              </datalist>
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            <Field stacked label="Contact number">
              <input className={inputCls} value={f.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
            </Field>
            <Field stacked label="WhatsApp">
              <input className={inputCls} value={f.whatsapp ?? ''} onChange={(e) => set('whatsapp', e.target.value)} />
            </Field>
            <Field stacked label="Email">
              <input className={inputCls} value={f.email ?? ''} onChange={(e) => set('email', e.target.value)} />
            </Field>
            <Field stacked label="Doctor / dentist name">
              <input className={inputCls} value={f.doctorName ?? ''} onChange={(e) => set('doctorName', e.target.value)} placeholder={doctorMatch?.name ?? ''} />
            </Field>
            <Field stacked label="Branch">
              <input className={inputCls} value={f.branch ?? ''} onChange={(e) => set('branch', e.target.value)} />
            </Field>
            <Field stacked label="Patient ID / code">
              <input className={inputCls} value={f.patientCode ?? ''} onChange={(e) => set('patientCode', e.target.value)} />
            </Field>
          </div>
        </section>
      )}

      {step === 1 && (
        <section className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            <Field stacked label="Case received through">
              <select className={inputCls} value={f.receivedThrough ?? ''} onChange={(e) => set('receivedThrough', e.target.value || null)}>
                <option value="">-</option>
                {cfg.optionsOf('received_through').map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field stacked label="Delivery date" hint="Set by the turnaround below; change it only if a different day was agreed.">
              <input type="date" className={inputCls} value={f.dueDate ?? ''} onChange={(e) => set('dueDate', e.target.value || null)} />
            </Field>
          </div>
          <TurnaroundPicker
            value={f.priority ?? 'normal'}
            onChange={(p) => {
              // Moving the tier moves the promised date with it, unless someone has already put a
              // specific day in the field above.
              const keep = f.dueDate && !isDerivedDueDate(f.dueDate, f.receivedAt ?? today, f.priority ?? 'normal');
              setF((cur) => ({
                ...cur,
                priority: p,
                dueDate: keep ? cur.dueDate : turnaroundDueDate(cur.receivedAt ?? today, p),
              }));
            }}
            receivedAt={f.receivedAt ?? today}
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field stacked label="Case type *">
              <select className={inputCls} value={f.caseTypes[0] ?? ''} onChange={(e) => set('caseTypes', e.target.value ? [e.target.value] : [])}>
                <option value="">- choose case type -</option>
                {caseTypes.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field stacked label="Notes">
              <input className={inputCls} value={f.notes ?? ''} onChange={(e) => set('notes', e.target.value)} placeholder="Instructions from the doctor" />
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field stacked label="Aligner trays" hint="Number of trays/steps - sets the clinic price.">
              <input
                type="number"
                min={0}
                className={inputCls}
                value={f.trays ?? ''}
                onChange={(e) => set('trays', e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0))}
                placeholder="e.g. 20"
              />
            </Field>
            <Field stacked label="Discount" hint="Optional - a percentage or a PKR amount off the aligner price.">
              <DiscountInput
                value={alignerDiscountOf(f)}
                onChange={(d) => {
                  set('discountType', d?.type ?? null);
                  set('discountValue', d?.value ?? null);
                }}
              />
            </Field>
          </div>
          {typeof f.trays === 'number' && f.trays > 0 && (
            <p className="text-sm text-muted">
              <QuoteLine quote={alignerQuote(f.trays, pricing, alignerDiscountOf(f))} suffix="to the clinic" />
            </p>
          )}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field stacked label="Additional notes">
              <textarea className={inputCls} rows={2} value={f.additionalNotes ?? ''} onChange={(e) => set('additionalNotes', e.target.value)} />
            </Field>
            <Field stacked label="Additional patient information">
              <textarea className={inputCls} rows={2} value={f.patientInfo ?? ''} onChange={(e) => set('patientInfo', e.target.value)} />
            </Field>
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="space-y-3">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Field stacked label="IPR">
              <select className={inputCls} value={f.iprStatus} onChange={(e) => set('iprStatus', e.target.value as WfCaseCreate['iprStatus'])}>
                {WF_REQ_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {WF_REQ_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
            <Field stacked label="Etching guide">
              <select className={inputCls} value={f.etchingStatus} onChange={(e) => set('etchingStatus', e.target.value as WfCaseCreate['etchingStatus'])}>
                {WF_REQ_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {WF_REQ_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
            <Field stacked label="Assign to">
              <select className={inputCls} value={f.assignedTo ?? ''} onChange={(e) => set('assignedTo', e.target.value || null)}>
                <option value="">- unassigned -</option>
                {cfg.staff.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field stacked label="Start in stage">
              <select className={inputCls} value={f.stageKey ?? R.start} onChange={(e) => set('stageKey', e.target.value)}>
                {cfg.stages.filter((s) => s.isActive && !s.isTerminal).map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field stacked label="Internal instructions">
              <textarea className={inputCls} rows={2} value={f.internalInstructions ?? ''} onChange={(e) => set('internalInstructions', e.target.value)} />
            </Field>
            <Field stacked label="Production instructions">
              <textarea className={inputCls} rows={2} value={f.productionInstructions ?? ''} onChange={(e) => set('productionInstructions', e.target.value)} />
            </Field>
            <Field stacked label="Patient instructions">
              <textarea className={inputCls} rows={2} value={f.patientInstructions ?? ''} onChange={(e) => set('patientInstructions', e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field stacked label="Delivery address">
              <input className={inputCls} value={f.deliveryAddress ?? ''} onChange={(e) => set('deliveryAddress', e.target.value)} />
            </Field>
            <Field stacked label="Manual case number" hint="Leave blank to auto-generate">
              <input className={`${inputCls} font-mono`} value={f.caseNumber ?? ''} onChange={(e) => set('caseNumber', e.target.value)} />
            </Field>
            <Field stacked label="Received at" hint="Defaults to now">
              <input type="datetime-local" className={inputCls} value={f.receivedAt ?? ''} onChange={(e) => set('receivedAt', e.target.value || null)} />
            </Field>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="space-y-2">
          <Field stacked label="Upload scans or images" hint="STL / PLY / OBJ / DCM scans, photos, X-rays, PDFs - optional, and you can add more later from the case page">
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                addFiles(e.dataTransfer.files);
              }}
              className="rounded border border-dashed border-line bg-surface-2/40 px-3 py-6 text-center"
            >
              <input
                type="file"
                multiple
                accept=".stl,.ply,.obj,.3mf,.dcm,.zip,image/*,application/pdf,video/*"
                className="block w-full text-sm text-ink file:mr-3 file:rounded file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-fg hover:file:opacity-90"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              <p className="mt-2 text-xs text-muted">…or drop files here</p>
            </div>
          </Field>
          {files.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {files.map((file) => (
                <li key={`${file.name}:${file.size}`} className="flex items-center gap-1.5 rounded-full bg-surface-2 px-2.5 py-1 text-xs text-ink">
                  <span className="max-w-[16rem] truncate">{file.name}</span>
                  <span className="text-muted">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                  <button type="button" aria-label={`Remove ${file.name}`} className="text-muted hover:text-status-urgent-fg" onClick={() => setFiles((prev) => prev.filter((x) => x !== file))}>
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {step === 4 && <Review f={f} fullName={fullName} files={files} doctorLabel={doctorMatch?.name ?? f.doctorName ?? null} clinicLinked={!!clinicId} onEdit={setStep} />}

      <WizardFooter
        step={step}
        last={STEPS.length - 1}
        canContinue={canContinue}
        busy={busy}
        progress={progress}
        variant="inline"
        submitLabel={busy ? (progress ? 'Uploading…' : 'Creating…') : files.length ? `Create case + ${files.length} file${files.length > 1 ? 's' : ''}` : 'Create case'}
        onBack={() => setStep((s) => Math.max(0, s - 1))}
        onNext={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
        onSubmit={() => void submit()}
        onCancel={onClose}
      />
    </div>
  );
}

function Review({
  f,
  fullName,
  files,
  doctorLabel,
  clinicLinked,
  onEdit,
}: {
  f: WfCaseCreate;
  fullName: string;
  files: File[];
  doctorLabel: string | null;
  /** Whether the typed clinic name resolved to a clinic the lab actually has on record. */
  clinicLinked: boolean;
  onEdit: (s: number) => void;
}) {
  const pricing = useAlignerPricing();
  const cfg = useWfConfig();
  const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
    <div className="flex justify-between gap-4 border-b border-line py-1.5 last:border-0">
      <span className="text-sm text-muted">{k}</span>
      <span className="text-right text-sm font-medium text-ink">{v || <span className="text-muted">-</span>}</span>
    </div>
  );
  const Head = ({ title, step }: { title: string; step: number }) => (
    <div className="mb-1 flex items-center justify-between">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{title}</h3>
      <button type="button" className="text-xs text-accent hover:underline" onClick={() => onEdit(step)}>
        Edit
      </button>
    </div>
  );
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <Head title="Patient & clinic" step={0} />
        <Row k="Name" v={fullName} />
        <Row k="Clinic" v={f.clinicName} />
        {f.clinicName && !clinicLinked && (
          <Row
            k=""
            v={
              <span className="text-status-warn-fg">
                Not matched to a clinic on record - the case is still created, but it will not join
                up with that clinic&rsquo;s history.
              </span>
            }
          />
        )}
        <Row k="Group ID" v={f.groupId} />
        <Row k="Doctor" v={doctorLabel} />
        <Row k="Contact" v={[f.phone, f.whatsapp, f.email].filter(Boolean).join(' · ')} />
      </div>
      <div>
        <Head title="Case details" step={1} />
        <Row k="Case type" v={f.caseTypes.map((t) => cfg.optionLabel('case_type', t)).join(', ')} />
        <Row
          k="Aligner trays"
          v={
            typeof f.trays === 'number' && f.trays > 0
              ? `${f.trays} trays · PKR ${alignerQuote(f.trays, pricing, alignerDiscountOf(f)).netRupees.toLocaleString()}${alignerDiscountOf(f) ? ' after discount' : ''}`
              : null
          }
        />
        <Row k="Received through" v={cfg.optionLabel('received_through', f.receivedThrough)} />
        <Row
          k="Delivery"
          v={`${turnaroundOf(f.priority).label} - ${turnaroundOf(f.priority).days} days${
            turnaroundSurchargeRupees(f.priority) ? ` · PKR ${turnaroundSurchargeRupees(f.priority).toLocaleString('en-PK')} extra` : ''
          }`}
        />
        <Row k="Delivery date" v={f.dueDate} />
        <Row k="Notes" v={f.notes} />
      </div>
      <div>
        <Head title="Lab setup" step={2} />
        <Row k="IPR" v={WF_REQ_LABELS[f.iprStatus ?? 'not_required']} />
        <Row k="Etching guide" v={WF_REQ_LABELS[f.etchingStatus ?? 'not_required']} />
        <Row k="Assign to" v={cfg.staffName(f.assignedTo)} />
        <Row k="Start in stage" v={cfg.stageName(f.stageKey ?? R.start)} />
        <Row k="Production instructions" v={f.productionInstructions} />
      </div>
      <div>
        <Head title={`Records (${files.length})`} step={3} />
        {files.length ? (
          <ul className="flex flex-wrap gap-1.5 pt-1">
            {files.map((file) => (
              <li key={`${file.name}:${file.size}`} className="rounded-full bg-surface-2 px-2.5 py-1 text-xs text-ink">
                {file.name} <span className="text-muted">· {(file.size / 1024 / 1024).toFixed(1)} MB</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="pt-1 text-xs text-muted">No files attached. They can be added from the case page at any time.</p>
        )}
      </div>
    </section>
  );
}
